import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatTile, StatusBadge, Overline } from "@/components/ui-bits";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Wallet, TrendingUp, TrendingDown, Bot, AlertTriangle, Clock,
  ShoppingCart, MessagesSquare, Sparkles, Zap, Activity, CheckCircle2, AlertCircle,
} from "lucide-react";

export default function Console() {
  const [d, setD] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/console/overview").then((r) => setD(r.data)).catch(() => {});
  const loadJobs = () => api.get("/jobs/health").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadJobs();
    const i = setInterval(() => { load(); loadJobs(); }, 12000);
    return () => clearInterval(i);
  }, []);
  useRealtime(() => load());

  const simulateSale = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/simulate/sale");
      toast.success(`${data.agent} closed a sale for ${money(data.amount)}`, { description: "Revenue booked to treasury · 20% agent retention" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create an active agent first");
    } finally {
      setBusy(false);
    }
  };

  if (!d) return <div className="text-slate-500 font-mono text-sm">Loading telemetry…</div>;
  const c = d.agent_counts || {};

  return (
    <div className="space-y-6" data-testid="console-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Operator Console</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Command & Telemetry</h1>
        </div>
        <button
          data-testid="simulate-sale-btn"
          onClick={simulateSale}
          disabled={busy}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-500/25 transition-colors disabled:opacity-60 ${d.demo_mode === false ? "hidden" : ""}`}
        >
          <Sparkles className="w-4 h-4" /> Simulate Agent Sale ($149)
        </button>
      </div>

      <OnboardingChecklist refreshKey={d} />

      {d.frozen && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-center gap-3" data-testid="freeze-banner">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-sm text-red-300 font-medium">Fleet is FROZEN — all agent spend and autonomy is halted. Lift the freeze from the header.</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile testid="stat-treasury" label="Treasury Balance" value={money(d.treasury_balance)} sub="Available capital" accent="emerald" icon={Wallet} />
        <StatTile testid="stat-revenue" label="Total Revenue" value={money(d.total_revenue)} sub="Booked earnings" accent="cyan" icon={TrendingUp} />
        <StatTile testid="stat-spend" label="Total Spend" value={money(d.total_spend)} sub="Agent outflows" accent="amber" icon={TrendingDown} />
        <StatTile testid="stat-anomalies" label="Critical Events" value={d.anomalies} sub="Freezes & hard-stops" accent="crimson" icon={AlertTriangle} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Fleet health */}
        <div className="hud-card rounded-lg p-5">
          <Overline>Fleet Health</Overline>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <FleetStat label="Active" value={c.active || 0} color="text-emerald-400" />
            <FleetStat label="Paused" value={c.paused || 0} color="text-amber-400" />
            <FleetStat label="Frozen" value={c.frozen || 0} color="text-red-400" />
            <FleetStat label="Killed" value={c.killed || 0} color="text-slate-400" />
          </div>
          <div className="mt-5 pt-4 border-t border-slate-800 grid grid-cols-3 gap-2 text-center">
            <Mini icon={Bot} label="Agents" value={d.total_agents} />
            <Mini icon={ShoppingCart} label="Paid" value={d.paid_orders} />
            <Mini icon={MessagesSquare} label="Chats" value={d.open_conversations} />
          </div>
        </div>

        {/* Triage: pending approvals */}
        <div className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": "#F59E0B" }}>
          <div className="flex items-center justify-between">
            <Overline>HITL Spend Queue</Overline>
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <div className="mt-6 text-center">
            <div className="font-mono font-black tabular-nums text-5xl text-amber-400" data-testid="pending-spends-count">{d.pending_spends}</div>
            <div className="mt-2 text-xs text-slate-500">spend requests awaiting human approval</div>
          </div>
          <a href="/treasury" className="mt-6 block text-center py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase tracking-wider hover:bg-amber-500/20 transition-colors">
            Open Approval Center
          </a>
        </div>

        {/* Activity stream */}
        <div className="hud-card rounded-lg p-5">
          <div className="flex items-center justify-between">
            <Overline>Live Activity Stream</Overline>
            <Zap className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="mt-4 space-y-2 max-h-72 overflow-y-auto" data-testid="activity-stream">
            {d.activity.length === 0 && <div className="text-xs text-slate-600 font-mono">No events yet.</div>}
            {d.activity.map((a) => (
              <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-start gap-2 text-xs border-b border-slate-800/60 pb-2">
                <StatusBadge status={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-300 font-mono truncate">{a.action}</div>
                  <div className="text-slate-600 truncate">{a.actor} · {a.detail}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Autonomy loops telemetry */}
      {jobs && (
        <div className="hud-card rounded-lg p-5" data-testid="jobs-health-panel">
          <div className="flex items-center justify-between">
            <Overline>Autonomy Loops</Overline>
            <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider">
              <Activity className={`w-4 h-4 ${jobs.healthy === jobs.total ? "text-emerald-400" : "text-amber-400"}`} />
              <span className={jobs.healthy === jobs.total ? "text-emerald-400" : "text-amber-400"} data-testid="jobs-health-count">{jobs.healthy}/{jobs.total} healthy</span>
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {jobs.jobs.map((j) => {
              const good = j.last_ok && !j.stale;
              const rel = j.last_run_at ? timeAgo(j.last_run_at) : "never run";
              return (
                <div key={j.id} data-testid={`job-${j.id}`} title={j.last_error || rel}
                  className={`rounded-md border px-2.5 py-2 ${good ? "border-slate-800 bg-[#080B11]" : "border-amber-500/30 bg-amber-500/[0.05]"}`}>
                  <div className="flex items-center gap-1.5">
                    {good ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" /> : <AlertCircle className="w-3 h-3 text-amber-400 shrink-0" />}
                    <span className="font-mono text-[10px] text-slate-300 truncate">{j.id}</span>
                  </div>
                  <div className="text-[9px] font-mono text-slate-600 mt-1">{rel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function FleetStat({ label, value, color }) {
  return (
    <div className="rounded-md border border-slate-800 bg-[#080B11] p-3">
      <div className={`font-mono font-bold tabular-nums text-2xl ${color}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div>
      <Icon className="w-4 h-4 text-slate-500 mx-auto" />
      <div className="font-mono font-bold tabular-nums text-lg mt-1">{value}</div>
      <div className="text-[9px] font-mono uppercase tracking-wider text-slate-600">{label}</div>
    </div>
  );
}
